import React from "react";
import { useTranslation } from "react-i18next";
import { Alert, Box, Chip, Typography } from "@mui/material";
import color from "../../../Constants/colors";
import {
  DocumentCraftingOutline,
  DocumentCraftingOutlineItem,
} from "../../../Models/documentCrafting";

/** "preise-2" -> "Preise 2", "key_figures" -> "Key figures". */
const humanize = (block: string) => {
  const text = block.replace(/[-_]+/g, " ").trim();
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : block;
};

/** Localised name of an outline block (A4 block or deck block/slide kind). */
export const useBlockName = () => {
  const { t } = useTranslation("documentCrafting");
  return (block: string) =>
    t(`run.outline.blocks.${block}`, { defaultValue: humanize(block) });
};

interface Group {
  unit: "page" | "slide" | null;
  number: number | null;
  items: DocumentCraftingOutlineItem[];
}

// Consecutive items of the same page or slide form one group.
const groupItems = (items: DocumentCraftingOutlineItem[]): Group[] =>
  items.reduce<Group[]>((groups, item) => {
    const unit = item.slide !== undefined && item.slide !== null ? "slide" : item.page ? "page" : null;
    const number = unit === "slide" ? (item.slide ?? null) : unit === "page" ? (item.page ?? null) : null;
    const last = groups[groups.length - 1];
    if (last && last.unit === unit && last.number === number) {
      last.items.push(item);
      return groups;
    }
    return [...groups, { unit, number, items: [item] }];
  }, []);

/** The proposed structure of the document, read-only (questions step). */
const OutlinePreview: React.FC<{ outline: DocumentCraftingOutline | null | undefined }> = ({
  outline,
}) => {
  const { t } = useTranslation("documentCrafting");
  const blockName = useBlockName();
  const items = outline?.items ?? [];
  const missing = outline?.missing_required_facts ?? [];

  if (!items.length && !missing.length) {
    return (
      <Typography sx={{ fontSize: 14, color: color.gray500 }}>{t("run.outline.empty")}</Typography>
    );
  }

  return (
    <Box>
      {missing.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t("run.outline.missingFacts")}
          <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
            {missing.map((fact, index) => (
              <li key={`${index}-${fact}`}>{fact}</li>
            ))}
          </Box>
        </Alert>
      )}
      <Box component="ol" sx={{ m: 0, p: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 1.5 }}>
        {groupItems(items).map((group, groupIndex) => (
          <Box
            component="li"
            key={`${group.unit}-${group.number}-${groupIndex}`}
            sx={{ display: "grid", gridTemplateColumns: "72px minmax(0, 1fr)", gap: 1.5 }}
          >
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: color.gray500, pt: 1 }}>
              {group.unit && group.number !== null
                ? t(`run.outline.${group.unit}`, { number: group.number })
                : "–"}
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {group.items.map((item, index) => {
                const keywords = item.keywords ?? [];
                const sources = item.sources ?? [];
                return (
                <Box
                  key={`${item.block}-${index}`}
                  sx={{
                    p: 1.25,
                    borderRadius: "10px",
                    border: `1px solid ${color.gray200}`,
                    backgroundColor: color.white,
                  }}
                >
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: color.gray900 }}>
                    {blockName(item.block)}
                  </Typography>
                  {item.purpose && (
                    <Typography sx={{ fontSize: 13, color: color.gray700, mt: 0.25 }}>
                      {item.purpose}
                    </Typography>
                  )}
                  {(keywords.length > 0 || sources.length > 0) && (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.75, alignItems: "center" }}>
                      {keywords.map((keyword, keywordIndex) => (
                        <Chip
                          key={`${keyword}-${keywordIndex}`}
                          size="small"
                          label={keyword}
                          sx={{ height: 22, fontSize: 12, backgroundColor: color.gray100, color: color.gray700 }}
                        />
                      ))}
                      {sources.length > 0 && (
                        <Typography sx={{ fontSize: 12, color: color.gray500, ml: 0.5 }}>
                          {t("run.outline.sources", { count: sources.length })}
                        </Typography>
                      )}
                    </Box>
                  )}
                </Box>
                );
              })}
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default OutlinePreview;
